import React from 'react';
import * as CSS from 'csstype';
import styled, { css } from 'styled-components';
import { Box, Card } from '@99designs/emu';
import { boxShadows, colors, spaces } from '@99designs/emu/settings';
import { IconTick2 } from '@99designs/icons';

export interface ChoiceCardProps {
    id: string;
    children: React.ReactNode;
    color?: CSS.Property.Color | colors.Color;
    isSelected?: boolean;
    onSelection?: (value: string) => void;
}

const StyledBox = styled(Box)<Pick<ChoiceCardProps, 'isSelected'>>`
    border-radius: ${spaces['1bu']};

    &:focus-visible {
        box-shadow: ${boxShadows.focusRing}, ${boxShadows.medium};
        outline: none;
    }
`;

const StyledCard = styled(Card)<Pick<ChoiceCardProps, 'color' | 'isSelected'>>`
    position: relative;

    &:hover {
        box-shadow: inset 0 0 0 2px ${colors.grey300}, ${boxShadows.medium};
        cursor: pointer;
    }

    ${({ color, isSelected }) =>
        isSelected &&
        css`
            box-shadow: inset 0 0 0 ${spaces['0.25bu']} ${colors[color as colors.Color] || color},
                ${boxShadows.medium};

            &:hover {
                box-shadow: inset 0 0 0 ${spaces['0.25bu']}
                        ${colors[color as colors.Color] || color},
                    ${boxShadows.medium};
            }
        `}
`;

const StyledIcon = styled(IconTick2)<Pick<ChoiceCardProps, 'color' | 'isSelected'>>`
    position: absolute;
    top: ${spaces['0.75bu']};
    right: ${spaces['0.75bu']};
    opacity: 0;

    ${({ color, isSelected }) =>
        isSelected &&
        css`
            color: ${colors[color as colors.Color] || color};
            opacity: 1;
        `}
`;

export function ChoiceCard({
    id,
    children,
    color = colors.grey800,
    isSelected = false,
    onSelection,
}: ChoiceCardProps) {
    return (
        <StyledBox
            tabIndex={0}
            onClick={() => {
                if (onSelection instanceof Function) {
                    onSelection(id);
                }
            }}
            onKeyPress={(e) => {
                e.preventDefault();

                if (e.key === 'Enter' && onSelection instanceof Function) {
                    onSelection(id);
                }
            }}
            isSelected={isSelected}
        >
            <StyledCard color={color} isSelected={isSelected}>
                <StyledIcon color={color} isSelected={isSelected} />
                {children}
            </StyledCard>
        </StyledBox>
    );
}
